/// <reference path="./@types/images.d.ts" />

import { shallowRef } from 'vue';
import CAS from './sdk/cas';
import type { Settings } from './stores/modules/settings/types';
import { baspMenusFormat } from './modules/sf-basp';
import { request } from './apis/request';
import { type RecursiveRequired } from './@types/global';
import { useSettingsStore, useUserStore } from './stores';
import useNoticesStore from './stores/modules/notices';
import IconLayoutStyle from './components/Icon/IconLayoutStyle.vue';
import IconBack from './components/Icon/IconBack.vue';
import IconDownload from './components/Icon/IconDownload.vue';
import IconSfSearch from './components/Icon/IconSfSearch.vue';
import IconQuestionFilled from './components/Icon/IconQuestionFilled.vue';
import IconBellFilled from './components/Icon/IconBellFilled.vue';
import IconFeedbackHeader from './components/Icon/IconFeedbackHeader.vue';
import LayoutStyle from './components/LayoutStyle.vue';
import AppNotice from './components/AppNotice.vue';
import bgLBBlueSrc from './assets/images/bg-left-bottom-blue.png';
import bgLBRedSrc from './assets/images/bg-left-bottom-red.png';
import bgRTBlueSrc from './assets/images/bg-right-top-blue.png';
import bgRTRedSrc from './assets/images/bg-right-top-red.png';
import { MENU_MODE, FEEDBACK_PLACEMENT } from './const';
import { routerNotInSetup } from './utils/routerNotInSetup';
import { getEmpCode } from './utils';
import { onSensorsWebClick } from './plugins/sensors';
// import IconWaybill from './components/Icon/IconWaybill.vue'; // 运单查询
import IconProduct from './assets/images/产品查询.png'; // 产品查询
import IconExpress from './assets/images/收寄标准.png'; // 收寄标准
import IconPaybill from './assets/images/运费时效.png'; // 运费时效
// import IconRouteQuery from './assets/images/路由代码.png'; // 路由代码
// import IconAssistant from './components/Icon/IconAssistant.vue'; // 智能助手
// import IconAppDownload from './components/Icon/IconAppDownload.vue'; // App下载
// import IconSysStatis from './components/Icon/IconSysStatis.vue'; // 系统分析
import chatBIDefaultIcon from './components/ChatBI/icon.gif';
// @ts-ignore
import zhCn from 'element-plus/dist/locale/zh-cn';
import lowCodeDefaultIcon from './components/LowCode/logo.png';

/**
 * 默认配置项不建议修改，请修改settings.ts文件
 */
const defaultSettings: RecursiveRequired<Settings> = {
  api: {
    baseURL: '/o-apis',
  },
  system: {
    systemCode: '',
    logo: '',
    faviconIco: '',
    webTitle: '主系统名称',
    webSubTitle: '-子系统名称',
    getDocumentTitle: (targetTitle?: string) => {
      const {
        settings: {
          system: { webTitle, webSubTitle },
        },
      } = useSettingsStore();
      if (!targetTitle) {
        return `${webTitle}${webSubTitle}`;
      }

      const title = (webSubTitle || webTitle).replace(/^-/, '');
      return `${targetTitle}-${title}`;
    },
    onLogoClick: (router, homePath) => {
      router.push(homePath || '/');
    },
    openUserConfigCustomStorage: true,
    async getUserConfig() {
      const userId = getEmpCode();
      const userConfigCacheKey = `USER-CONFIG-CACHE-KEY__${this.systemCode}__${userId}`;
      let list: { key: string; value: string }[] = [];
      try {
        list = JSON.parse(window.localStorage.getItem(userConfigCacheKey) || '[]');
      } catch (e) {
        //
      }
      return list;
    },
    async setUserConfig({ key, value }) {
      const userId = getEmpCode();
      const userConfigCacheKey = `USER-CONFIG-CACHE-KEY__${this.systemCode}__${userId}`;
      let list: { key: string; value: string }[] = [];
      try {
        list = JSON.parse(window.localStorage.getItem(userConfigCacheKey) || '[]');
      } catch (e) {
        //
      }
      const index = list.findIndex((item) => item.key === key);
      if (index > -1) {
        list[index] = { key, value };
      } else {
        list.push({ key, value });
      }
      window.localStorage.setItem(userConfigCacheKey, JSON.stringify(list));
    },
    async batchSetUserConfig(list) {
      const userId = getEmpCode();
      const userConfigCacheKey = `USER-CONFIG-CACHE-KEY__${this.systemCode}__${userId}`;
      window.localStorage.setItem(userConfigCacheKey, JSON.stringify(list));
    },
    mainAppFramework: 'qiankun',
  },
  theme: {
    layoutSize: 'loose',
    style: 'blue',
    layoutStyle: 'light',
    mode: 'light',
    leftBottomBgImg: (theme) => (theme === 'blue' ? bgLBBlueSrc : bgLBRedSrc),
    rightTopBgImg: (theme) => (theme === 'blue' ? bgRTBlueSrc : bgRTRedSrc),
    rightTopBgStyle: 'no-repeat right top/572px 502px',
    leftBottomBgStyle: 'no-repeat left bottom/408px 365px',
  },
  // @ts-ignore
  elConfigProvider: { namespace: 'sf-olayout', locale: zhCn },
  layout: {
    hiddenNewCodeTool: true,
    hiddenTopHeader: false,
    hiddenUserSettings: false,
    hiddenFavorites: false,
    slot: { header: { afterLogo: undefined }, logo: undefined },
  },
  tabBar: {
    show: true,
    showFeatureStatisticsAnalysis: true,
    ignoreTabBarPersistent: false,
    cacheType: 'localStorage',
    maxCount: 0,
    scrollbar: {
      trigger: 'none',
    },
    transformMenuUrl: (menuUrl: string, base?: string) => {
      return base ? `${base}${menuUrl}` : menuUrl;
    },
    onJumpFeatureStatisticsAnalysisPage: (router, { systemCode, selectedMenuUrl }) => {
      router.push(
        `/feature-statistics-analysis?systemCode=${systemCode}&selectedMenuUrl=${encodeURIComponent(selectedMenuUrl)}`,
      );
    },
    onDblClick: () => {},
    onBeforeClose: () => {},
  },
  route: {
    baseOn: 'filesystem',
    baseURL: '',
    homeTitle: '工作台',
    homePath: '/',
    getRedirectPath: null,
    requestIdleCallbackTimeout: 100,
    getNotFoundTabTitle: (route) => {
      const { query } = route;
      return query?.title?.toString() || '';
    },
  },
  basp: {
    baspSystemKey: '',
    baspSubSystemCode: '',
  },
  login: {
    type: 'cas',
    onBeforeLogin: () => {},
    onLogin: async (type, config, router) => {
      if (type === 'cas') {
        if (!config?.appKey || !config?.appSecret) {
          throw Error('请先在 login 对象中配置 appKey 及 appSecret 属性');
        }

        const cas = new CAS({
          ...config,
          loginSuccess: (res) => {
            window.localStorage.setItem(`CAS_LOGIN_RES__${config.appKey}`, JSON.stringify(res?.obj));
            config.loginSuccess?.(res);
          },
        });
        (window as any).casService = cas;

        // 如果存在 ticket，优先走 cas login
        const search = new URLSearchParams(window.location.search);
        if (search.get('ticket')) {
          await router?.isReady();
          const res = await cas.login();
          return { userId: res.userId, appKey: config.appKey };
        }

        const res = await cas.checkToken();
        return res;
      }
      console.error('自定义登录需在配置中重写onLogin');
      throw Error('自定义登录需在配置中重写onLogin');
    },
    onLoginSuccess: () => {},
    onLoginFailed: () => {},
    onBeforeLogout: () => {},
    onLogout: async (type, config) => {
      if (type === 'cas') {
        if ((window as any).casService) {
          (window as any).casService.logout();
        } else {
          const cas = new CAS(config);
          (window as any).casService = cas;
          cas.logout();
        }
      } else {
        console.error('自定义登录需在配置中重写onLogout');
        throw Error('自定义登录需在配置中重写onLogout');
      }
    },
    onAfterLogout: () => {},
  },
  language: {
    value: 'zh_CN',
  },
  menu: {
    baseOn: 'basp',
    menuMode: 'side',
    orderType: 'descending-order',
    getFavoritesUrl: '/favorites',
    postFavoritesUrl: '/favorites/update',
    loadingDelayTime: 500,
    showMainPageAfterGetMenus: false,
    showSubPageAfterGetMenus: false,
    showLoadingPageUntilSubAppLoaded: true,
    showLoadingPageOnSwitchSubAppPage: true,
    shouldRefreshMenuOnRoleListChange: true,
    shouldRefreshMenuOnRoleChange: true,
    showIconInHeaderMenu: false,
    showIconInSubMenu: false,
    showHeaderSubMenuInHeadSideMode: false,
    favoritesDraggable: false,
    setActiveTopMenu: null,
    setActiveSideMenu: null,
    tooltipConfig: {},
    uniqueOpened: false,
    favoritesMaxHeight: '242px',
    favoritesIgnoreFilterOnRoleChange: false,
    favoritesPageDisplay: 'dropdown',
    horizontalMenuShowLevel: 0,
    async getMenus(baseOn) {
      if (baseOn === 'frontend') {
        return { success: true, obj: [] };
      }

      const res = await request({ url: '/dev-basp-user/indexModule/queryMenusTree', method: 'get' });
      return { success: res.success, obj: baspMenusFormat(res?.obj) };
    },
    async onClickMenuItem() {},
    async getFilteredListOnSearch(keyword, dataSource) {
      return dataSource.filter((item) => {
        const val = keyword.toLowerCase();
        return item.title.toLowerCase().includes(val) || item.moduleDesc?.toLowerCase().includes(val);
      });
    },
    async getFavorites(getFavoritesUrl) {
      try {
        const res = await request<string[]>({
          url: getFavoritesUrl,
          method: 'get',
        });
        return res;
      } catch (error) {
        return {
          success: false,
          obj: [] as string[],
        };
      }
    },
    async postFavorites(postFavoritesUrl, favorites) {
      try {
        const res = await request<string[]>({
          url: postFavoritesUrl,
          method: 'post',
          data: { favorites },
        });
        return res;
      } catch (error) {
        return {
          success: false,
          obj: undefined,
        };
      }
    },
  },
  user: {
    async getUserInfo() {
      const res = await request({ url: '/app/web/user/info', method: 'get' });
      return { success: res.success, obj: res?.obj };
    },
  },
  role: {
    shouldCacheCurrentRole: true,
    async getRoles() {
      const res = await request({ url: '/dev-basp-user/indexModule/queryRoles', method: 'get' });
      return { success: res.success, obj: res?.obj };
    },
  },
  errorConfig: {
    hideErrorAlert: false,
    hideAssets404: false,
    onErrorAlert: undefined,
  },
  menuSearch: {
    placement: 'head',
    empty: '暂无数据',
  },
  personalCenter: [
    {
      id: 'back',
      label: '返回原版',
      show: true,
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconBack),
      jumpUrl: '',
    },
    {
      id: 'layoutStyle',
      label: '风格设置',
      show: true,
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconLayoutStyle),
      comp: shallowRef(LayoutStyle),
      showSidebar: true,
      layoutStyleExtendList: [],
    },

    {
      id: 'download',
      label: '下载',
      show: true,
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconDownload),
      jumpUrl: '/download',
    },
    {
      id: 'help',
      label: '帮助',
      show: true,
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconQuestionFilled),
      jumpUrl: '',
    },
    {
      id: 'feedback',
      label: '意见反馈',
      show: () => {
        const settingsStore = useSettingsStore();
        const { feedbackConfig } = settingsStore.settings;
        return !!(feedbackConfig?.show && feedbackConfig?.placement === FEEDBACK_PLACEMENT.个人中心);
      },
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconFeedbackHeader),
      onClick: () => {
        (window as any).feedbackSdk?.open();
        onSensorsWebClick({
          title: '意见反馈',
          source: 'button-feedback',
        });
      },
    },
    {
      id: 'notice',
      label: '消息',
      show: true,
      placement: 'outside',
      type: 'button',
      props: {},
      icon: shallowRef(IconBellFilled),
      comp: shallowRef(AppNotice),
      badge() {
        const noticesStore = useNoticesStore();
        return noticesStore.unreadCount;
      },
      jumpUrl: '',
      queryNoticeUrl: '/message',
      readNoticeUrl: '/message/read',
    },
    {
      id: 'auth',
      label: '权限',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'select',
      props: {},
      options: () => {
        const userStore = useUserStore();
        return userStore.roleData.map((item) => ({ label: item.roleName, value: item.roleCode }));
      },
    },
    {
      id: 'lang',
      label: '语言',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'select',
      props: {},
      optionsJson: JSON.stringify([
        { label: '中文简体', value: 'zh_CN' },
        { label: '中文繁體', value: 'zh_TW' },
        { label: 'English', value: 'en_US' },
      ]),
    },
    {
      id: 'theme',
      label: '主题',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'select',
      props: {},
      optionsJson: JSON.stringify([
        { label: '红色主题', value: 'red', iconColor: { default: '#CC1B23', hover: '#D93F41', active: '#A60D1A' } },
        { label: '蓝色主题', value: 'blue', iconColor: { default: '#3355FF', hover: '#5C7CFF', active: '#2B42D9' } },
      ]),
    },
    {
      id: 'theme-mode',
      label: '模式',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'select',
      props: {},
      optionsJson: JSON.stringify([
        { label: '浅色模式', value: 'light' },
        { label: '深色模式', value: 'dark' },
      ]),
    },
    {
      id: 'menu-mode',
      label: '导航',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'select',
      props: {},
      // options: Object.entries(DICT_MENU_MODE).map(([label, value]) => ({ label, value })),
      options: MENU_MODE.map((value) => ({ label: 'const.MENU_MODE.' + value, value })),
    },
    // 分割线
    {
      id: 'menu',
      label: '菜单管理',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'button',
      props: { type: '', text: true, bg: true },
      jumpUrl: '/menu-manage',
    },
    {
      id: 'system',
      label: '系统设置',
      placement: 'personal-center-dropdown',
      type: 'button',
      props: { type: '', text: true, bg: true },
      show: true,
    },
    // 分割线
    {
      id: 'logout',
      label: '退出登录',
      show: true,
      placement: 'personal-center-dropdown',
      type: 'button',
      props: { type: 'danger', plain: true },
    },
  ],
  casConfig: {
    appKey: '',
    appSecret: '',
    basePath: '',
    casDomain: '',
    serviceUrl: '',
    isReturnCallBackResult: false,
    loginSuccess: () => {},
    loginFailed: ({ success, errorMessage, ...args }, ...config) => {
      if (!success) {
        // 避免被 / 路径中自动跳 homePath 影响
        setTimeout(() => {
          routerNotInSetup.value?.push(`/un-support-user?errorMessage=${errorMessage}`);
        });
      }
      throw new Error(errorMessage);
    },
  },
  sentryConfig: {
    dsn: '',
    initData: {},
    profileData: {},
  },
  sensorsConfig: {
    initData: {},
    registerData: {},
    profileData: {},
    isManualReport: false,
    platform_type: 'Web',
    platform_name: 'OWF_PC',
    system_code: '',
    product_name: '',
    onWebClick: (options) => {
      const {
        settings: { system, sensorsConfig },
      } = useSettingsStore();
      if (!sensorsConfig.system_code) {
        return;
      }

      const sensors = (window as any).sensorsDataAnalytic201505;

      const extraData: Record<string, any> = {};
      if (options.to && system.getDocumentTitle) {
        extraData.$title = system.getDocumentTitle(options.title);
      }

      sensors?.track('$WebClick', {
        $element_content: options.title,
        ...extraData,
        ...options.to,
        menu_url: options.menu_url,
        menu_name: options.menu_name,
      });
    },
  },
  feedbackConfig: {
    show: true,
    placement: FEEDBACK_PLACEMENT.浮窗,
    showBubble: false,
    // urlMatches: /\S/,
    // showHeader: true,
    appId: '',
    appSecret: '',
    onOpen: () => {},
    onSubmitSuccess: () => {},
    onClose: () => {},
    // showIcon: false,
    appCode: '',
    // gapWidth: 10,
    // gapTo: 100,
    // gapBottom: 60,
    // sendUrl: '/eosFmsSfpsServices/userFeedback/openFeedback',
  },
  ratingConfig: {
    show: true,
    appId: '',
    appSecret: '',
    appCode: '',
    appName: '',
    locale: 'zh-CN',
    period: 'weekly',
    showDelay: 3,
    modules: [],
    right: 30,
    bottom: 30,
    zIndex: 1000,
    onOpen: () => {},
    onClose: () => {},
    onSubmitSuccess: () => {},
  },
  toolboxConfig: {
    show: false,
    // 跳转页面
    pages: [
      // {
      //   label: '运单查询',
      //   icon: shallowRef(IconWaybill),
      //   onClick() {
      //     console.log('返回111');
      //   },
      // },
      {
        label: '产品查询',
        icon: IconProduct,
        iconType: 'img',
        onClick() {
          window.open('http://product-family.sf-express.com/home', '_blank');
        },
      },
      {
        label: '收寄标准',
        icon: IconExpress,
        iconType: 'img',
        onClick() {
          window.open('https://www.sf-express.com/chn/sc/accept-query', '_blank');
        },
      },
      {
        label: '运费时效',
        icon: IconPaybill,
        iconType: 'img',
        onClick() {
          window.open('https://www.sf-express.com/chn/sc/price-query', '_blank');
        },
      },
      // {
      //   label: '路由代码',
      //   hide: true,
      //   icon: IconRouteQuery,
      //   iconType: 'img',
      //   onClick() {
      //     console.log('返回222');
      //   },
      // },
    ],
    // 快捷工具
    tools: [],
  },
  chatBIConfig: {
    assistantId: '',
    appId: '',
    token: '',
    v2: false,
    newSession: 1,
    lock: 0,
    icon: chatBIDefaultIcon,
    label: '',
  },
  lowCodeConfig: {
    show: false,
    icon: lowCodeDefaultIcon,
    label: '低代码',
    id: '',
    tenant: '',
    pkg: '',
  },
  subApps: [],
  isSubAppUrl({ subAppName, routeFullPath, settings }) {
    const standardRule = new RegExp(`^/${subAppName}([/?#].*)?$`).test(routeFullPath);
    return standardRule || settings.subApps?.length === 1;
  },
};

export default defaultSettings;
